import { Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, Renderer2, SimpleChanges, ViewChild } from '@angular/core';

import { DATE_TYPE_BY_DTO } from '../constants';
import { IAtxTableActions } from '../interfaces';

/**
    dark
 * * Display model into Table with next features:
 * - Sort
 * - Order
 * - Pagination
 */
@Component({
    selector: 'atx-table',
    template: `<h4 *ngIf="title">
    {{ title }}
</h4>

<table class="table table-{{ size }} table-responsive-md text-{{ align }}"
       [class.table-dark]="dark"
       [class.table-striped]="striped"
       [class.table-bordered]="bordered"
       [class.table-borderless]="borderless"
       [class.table-hover]="hover">
    <thead class="thead-{{ theadColor }}">
    <tr>
        <th *ngIf="hasParentChild || hasPrivate"></th>
        <th *ngFor="let th of headers"
            (click)="actions.sort?.without.indexOf(th) < 0 && sort(th)">
            {{ actions?.sort?.external[ th.title || th ] || th }}
            <i *ngIf="actions.sort?.without.indexOf(th) < 0"
               [ngClass]="getSortedClass(th)">
            </i>
        </th>
        <th *ngIf="hasActions"></th>
    </tr>
    </thead>
    <tbody #tableBody>
    <ng-container *ngFor="let element of model?.included">
        <ng-container
            *ngIf="!!element.children && !!element.children?.length; then parentChild else parent"></ng-container>
        <ng-template #parent>
            <ng-container *ngTemplateOutlet="template; context: {$implicit: element}"></ng-container>
        </ng-template>
        <ng-template #parentChild>
            <ng-container *ngTemplateOutlet="template; context: {$implicit: element}"></ng-container>
            <ng-container *ngFor="let children of element?.children">
                <ng-container
                    *ngTemplateOutlet="template; context: {$implicit: children, parent: element?.id}"></ng-container>
            </ng-container>
        </ng-template>
    </ng-container>
    </tbody>
</table>

<ng-template #template let-element let-parent="parent">
    <tr (click)="(hasSelectable) && selectRow(element)"
        class="accordion-toggle"
        [ngClass]="{ 'selected': hasSelected(element), 'hidden': parent, 'show': !parent }"
        id="{{ (!!parent) ? 'parent'+parent : element.id }}"
        [ngStyle]="actions?.rowBorderColor && { 'border-left': '10px solid ' + (element | load: actions?.rowBorderColor) }">
        <td *ngIf="hasParentChild"
            (click)="$event.stopPropagation();showChildrens(element.id);"
            [ngClass]="element?.children && 'col-collapse'">
            <i *ngIf="actions?.hasParentChild"
               [class]="actions?.hasParentChild[element?.children?.length? 'hasParent':'hasChild']?.icon"
               (click)="actions?.hasParentChild[element?.children?.length? 'hasParent':'hasChild']?.callback(element)"
               [title]="((!!actions?.hasParentChild?.hover) && actions?.hasParentChild?.hover[(element | load: (actions?.hasParentChild?.hasParent?.hover || actions?.hasParentChild?.hasChild?.hover))]) || element?.name || element?.title || element?.id"></i>
            <i *ngIf="element | load : actions?.hasPrivate?.isPrivate"
               [class]="actions?.hasPrivate?.icon"></i>
        </td>
        <td *ngFor="let key of headers">
            <div *ngIf="hasObject(key); then icon else text"></div>

            <ng-template #text>
                <div *ngIf="hasDate(key); then date else notDate"></div>
                <ng-template #date>
                    {{ element | load: key | date: 'dd/MM/yyyy HH:mm':'UTC'}}
                </ng-template>
                <ng-template #notDate>
                    {{ element | load: key }}
                </ng-template>
            </ng-template>
            <ng-template #icon>
                <i *ngIf="element | load: key?.icon?.title"
                   [class]="key?.icon?.clazz"
                   [title]="element  | load: key?.icon?.title || element[key?.icon?.title] || element[key?.icon?.title]?.title"
                   [ngStyle]="{color: element[key?.icon?.color]?.color}"></i>
            </ng-template>
        </td>
        <td *ngIf="hasActions">
            <ng-container *ngFor="let action of actions?.rows">
                <div *ngIf="action?.popConfirm; then withPopConfirm else withoutPopConfirm"></div>

                <ng-template #withoutPopConfirm>
                    <i *ngIf="!!(element | load: action?.hasActive) || action?.hasActive === 'edit' || action?.hasActive === 'add'"
                       [class]="action?.icon"
                       [title]="!!action.hover && action?.hover"
                       (click)="(action?.hasActive === 'add') ? action.callback(element) : $event.stopPropagation();action.callback(element)">
                    </i>
                </ng-template>

                <ng-template #withPopConfirm>
                    <atx-pop-confirm *ngIf="!!action?.hasActive || element | load: action?.hasActive"
                                     [title]="action?.popConfirm?.title"
                                     [config]="action?.popConfirm?.config"
                                     (onCancel)="onPopConfirmEvent(element, action?.popConfirm?.cancel)"
                                     (onConfirm)="onPopConfirmEvent(element, action?.popConfirm?.confirm)">
                    </atx-pop-confirm>
                </ng-template>
            </ng-container>
        </td>
    </tr>
</ng-template>

<atx-table-pagination *ngIf="model && hasPagination"
                      [model]="model"
                      [justifyContent]="justifyContentPagination"
                      (onChangePage)="fireChangePage.emit($event)">
</atx-table-pagination>
`,
    styles: [`table .bd-callout{margin-top:0;margin-bottom:0;border-left:.25rem solid transparent;border-radius:0}table .bd-callout-warning{border-left-color:#ffe30b}table .bd-callout-info{border-left-color:#5ba6ff}table .bd-callout-danger{border-left-color:#ff4253}table .bd-callout-primary{border-left-color:#c544ff}table .selected{background:#778899!important}.show{display:table-row}.hidden{display:none}`],
})

export class AtxTableComponent implements OnInit, OnChanges {
    /**
     * @ViewChilda tableBody
     */
    @ViewChild('tableBody') tableBody: ElementRef;

    /**
     * Properties required for maintenance features and usability of tables has : page, total, offset, included
     * - Included has it contain model of table
    */
    @Input() public readonly model;

    /**
     * Flag to manage has row has selectable or not
     */
    @Input() public hasSelectable: boolean;

    /**
     * Flag to indicate style tables has Dark or default
     */
    @Input() public readonly dark: boolean;

    /**
     * Title of table.
    */
    @Input() public readonly title: string;

    @Input() public readonly hover: boolean;

    /**
     * Flag to indicate style tables style has Stripped
    */
    @Input() public readonly striped: boolean;

    /**
     * Object to indicate from model, which element are header of table for show this value.
    */
    @Input() public readonly headers: string[];
    @Input() public readonly bordered: boolean;
    @Input() public readonly theadColor: string;
    @Input() public readonly borderless: boolean;

    /**
     * Inidicate with class default from boostrap, size of table.
    */
    @Input() public readonly size: string = 'lg';

    /**
     * When this attribute has True select all element of model into table and return elements selected.
    */
    @Input() public readonly hasSelectAll: boolean;

    /**
     * Object with elements selected into table by default.
    */
    @Input() public readonly rowsSelected: object;

    /**
     * Object indicate actions and callback to interact with table.
    */
    @Input() public readonly actions: IAtxTableActions;
    @Input() public readonly align: string = 'center';

    /**
     * Flag for manage if pagination elements, are visible or hidden
    */
    @Input() public readonly hasPagination: boolean = true;

    /**
     * When table has Selectable, this flag indicate if multi or single select
    */
    @Input() public readonly hasMultiselect: boolean = true;
    @Input() public readonly justifyContentPagination: string;

    /**
     * Event dispatch when sorted table
    */
    @Output('onSort') public readonly fireSort: EventEmitter<any> = new EventEmitter();

    /**
     * Event dispatch when change pagination of table
    */
    @Output('onChangePage') public readonly fireChangePage: EventEmitter<any> = new EventEmitter();

    /**
     * Event dispatch when select one row from Table, emit element select or Group element Selected when has multiple select.
     */
    @Output('onSelect') public readonly fireSelectRow: EventEmitter<any> = new EventEmitter<any>();

    public order: string;
    public sorted: string;
    public hasActions: boolean;
    public hasPrivate: boolean;
    public hasParentChild: boolean;
    public elementsSelected: object[] = [];
    public hasInit: boolean;

    constructor(private renderer: Renderer2, private elementRef: ElementRef) {}

    public ngOnInit() {}

    public ngOnChanges(changes: SimpleChanges) {
        this.hasActions = this.actions && !!this.actions.rows.length;
        this.hasParentChild = this.actions && !!this.actions.hasParentChild;
        this.hasPrivate = this.actions && !!this.actions.hasPrivate;
        this.hasSelectable = this.actions.rows.length ? true : this.hasSelectable;

        if (!!this.rowsSelected && this.model.included.length > 0 && !this.hasInit) {
            this.setRowsSelectsByDefault();
        }

        if (!!changes.hasSelectAll && changes.hasSelectAll.previousValue !== this.hasSelectAll) {
            this.onSelectAllElements();
        }
    }

    private setRowsSelectsByDefault() {
        this.rowsSelected['rows'].forEach((row) => {
            const selectRow = this.model.included.find((item) => item[this.rowsSelected['identifier']] == row);
            if (!!selectRow) {
                this.elementsSelected.push(selectRow);
            }
        });

        this.hasInit = true;
    }

    public getSortedClass(th) {
        const icons = (<any>this.actions.sort).icons;

        return this.sorted === th && this.order === 'asc'
            ? icons.asc
            : this.sorted === th && this.order === 'desc'
                ? icons.desc
                : icons.default;
    }

    public sort(label) {
        if (this.sorted && this.sorted === label) {
            this.toggleOrder();
        } else {
            this.order = 'asc';
        }

        this.sorted = label;
        this.fireSort.emit({ sort: this.sorted['title'] || this.sorted, order: this.order });
    }

    public onPopConfirmEvent(element, callback) {
        if (callback) {
            callback(element);
        }
    }

    public selectRow(elementSelected: object) {
        (!this.hasMultiselect)
            ? this.singleSelect(elementSelected)
            : this.multiSelect(elementSelected);
    }

    private singleSelect(elementSelected: object) {
        if (!!this.elementsSelected.find((el) => elementSelected === el)) {
            this.elementsSelected = [];
        } else {
            this.elementsSelected = [];
            this.elementsSelected.push(elementSelected);
        }

        this.fireSelectRow.emit(this.elementsSelected);
    }

    private multiSelect(elementSelected: object) {
        if (this.hasMultiselect && this.hasInit && this.elementsSelected.find((el) => elementSelected === el)) {
            this.elementsSelected.splice(this.elementsSelected.indexOf(elementSelected), 1);
        } else {
            this.elementsSelected.push(elementSelected);
        }

        this.fireSelectRow.emit(this.elementsSelected);
    }

    public onSelectAllElements() {
        this.elementsSelected = [];
        if (this.hasSelectAll) {
            this.model.included.forEach((item) => {
                this.elementsSelected.push(item);
            });
        }

        this.fireSelectRow.emit(this.elementsSelected);
    }

    public hasSelected(element) {
        return !!this.elementsSelected.find((el) => Number(element['id']) === Number(el['id']));
    }

    public hasObject(key): boolean {
        return typeof key === 'object';
    }

    public hasDate(key): boolean {
        return !!DATE_TYPE_BY_DTO.find((element) => element === (key.split('.').pop() || key));
    }

    public showChildrens(id) {
        if (!!id) {
            for (const key in this.tableBody.nativeElement.children) {
                if (Object.keys(this.tableBody.nativeElement.children[key])) {
                    const childElement = this.tableBody.nativeElement.children[key];
                    if (childElement.id === ('parent' + id)) {
                        if (childElement.className.indexOf('show') > 0) {
                            this.renderer.removeClass(childElement, 'show');
                            this.renderer.addClass(childElement, 'hidden');
                        } else {
                            this.renderer.removeClass(childElement, 'hidden');
                            this.renderer.addClass(childElement, 'show');
                        }
                    }
                }
            }
        }
    }

    private toggleOrder() {
        this.order = this.order === 'asc' ? 'desc' : 'asc';
    }

    public reset() {
        this.elementsSelected = [];
        this.fireSelectRow.emit(this.elementsSelected);
    }
}
